import { User } from "@tsTypes/schema";
import { UniversityExpertise } from "@tsTypes/universityProfiles";
import { capitalize } from "./textUtils";

export const sortByHasProfilePicture = <T extends Pick<User, "image"> & Record<string, unknown>>(
  users: T[],
  imageSize: "small" | "medium" | "large" = "medium"
): T[] => {
  return users.sort((a, b) => {
    return Number(Boolean(b.image?.[imageSize].url)) - Number(Boolean(a.image?.[imageSize].url));
  });
};

export const hasInvalidUniversityExpertise = (expertise: string): boolean => {
  return expertise.split(",").some((e) => {
    return !(Object.values(UniversityExpertise) as string[]).includes(e);
  });
};

type UserShortNameOptions = {
  canCapitalize: boolean;
};

export const userShortName = (
  user: {
    first_name?: string | null;
    last_name?: string | null;
    name?: string | null;
  },
  options: UserShortNameOptions = { canCapitalize: true }
): string => {
  const { canCapitalize } = options;

  if (user.first_name && user.last_name) {
    return canCapitalize
      ? `${capitalize(user.first_name)} ${capitalize(user.last_name)}`
      : `${user.first_name} ${user.last_name}`;
  }

  if (user.name) {
    const names = user.name.split(" ");
    const firstName = canCapitalize ? capitalize(names[0]) : names[0];
    const lastName = canCapitalize ? capitalize(names[names.length - 1]) : names[names.length - 1];
    return `${firstName} ${lastName}`;
  }

  throw new Error("first_name, last_name, or name is required");
};

const INVALID_SLUG_ERROR_MESSAGE = "Invalid innovator slug";

export const parseInnovatorSlug = (slug: string): number => {
  const trimmedSlug = slug?.trim();
  if (!trimmedSlug) {
    throw new Error(INVALID_SLUG_ERROR_MESSAGE);
  }

  const match = trimmedSlug.match(/^(\d+)(?:-.+)?$/) ?? trimmedSlug.match(/^(?:.+?-)+(\d+)$/);
  if (!match) {
    throw new Error(INVALID_SLUG_ERROR_MESSAGE);
  }

  const userId = Number.parseInt(match[1], 10);
  if (!Number.isFinite(userId) || userId <= 0) {
    throw new Error(INVALID_SLUG_ERROR_MESSAGE);
  }

  return userId;
};

export const innovatorSlugErrors = {
  INVALID_SLUG_ERROR_MESSAGE,
} as const;

export type InnovatorSlugErrors = typeof innovatorSlugErrors;
